// ─────────────────────────────────────────────────────────────
// ZIFEK — Profil d'entreprise & paramètres de facturation
// Le commerçant saisit ses informations une seule fois ; Zifek les
// réutilise dans les factures, devis, reçus et documents.
// Tout est scopé par `merchant_id` (isolation multi-tenant).
// ─────────────────────────────────────────────────────────────

export interface CompanyProfile {
  id?: number;
  merchant_id: number;
  // Identité
  company_name: string;
  legal_form: string;
  logo_url: string;
  // Adresse
  address: string;
  city: string;
  postal_code: string;
  country: string;
  // Coordonnées
  phone: string;
  email: string;
  website: string;
  // Informations légales
  ice: string;
  if_number: string;
  tp_number: string;
  rc_number: string;
  cnss_number: string;
  // Fiscalité
  vat_subject: boolean;
  default_vat_rate: number;
  // Banque
  bank_name: string;
  rib: string;
  iban: string;
  // Facturation
  invoice_prefix: string;
  invoice_number_format: string;
  next_invoice_seq: number;
  payment_terms: string;
  payment_methods: string;
  legal_mentions: string;
  invoice_footer: string;
  invoice_template: 'classic' | 'modern';
  invoice_language: 'fr' | 'ar' | 'en';
  currency: string;
  /** Voix Kokoro préférée du commerce pour la lecture des réponses de l'assistant. */
  tts_voice: string;
}

export const DEFAULT_COMPANY_PROFILE: Omit<CompanyProfile, 'merchant_id'> = {
  company_name: '',
  legal_form: '',
  logo_url: '',
  address: '',
  city: '',
  postal_code: '',
  country: 'Maroc',
  phone: '',
  email: '',
  website: '',
  ice: '',
  if_number: '',
  tp_number: '',
  rc_number: '',
  cnss_number: '',
  vat_subject: false,
  default_vat_rate: 20,
  bank_name: '',
  rib: '',
  iban: '',
  invoice_prefix: 'FAC',
  invoice_number_format: 'FAC-{YYYY}-{SEQ:5}',
  next_invoice_seq: 1,
  payment_terms: 'Paiement à réception',
  payment_methods: '',
  legal_mentions: '',
  invoice_footer: '',
  invoice_template: 'classic',
  invoice_language: 'fr',
  currency: 'MAD',
  tts_voice: '',
};

export const LEGAL_FORMS = [
  'Auto-entrepreneur',
  'SARL',
  'SARL AU',
  'SA',
  'SAS',
  'SNC',
  'Association',
  'Coopérative',
  'Autre',
];

export const INVOICE_LANGUAGES: { key: 'fr' | 'ar' | 'en'; label: string }[] = [
  { key: 'fr', label: 'Français' },
  { key: 'ar', label: 'العربية' },
  { key: 'en', label: 'English' },
];

export const INVOICE_TEMPLATES: { key: 'classic' | 'modern'; label: string }[] = [
  { key: 'classic', label: 'Classique' },
  { key: 'modern', label: 'Moderne' },
];

/** Fusionne un profil en base avec les valeurs par défaut. */
export function mergeCompanyProfile(
  db: Partial<CompanyProfile> | null | undefined,
  merchantId: number,
): CompanyProfile {
  return { ...DEFAULT_COMPANY_PROFILE, merchant_id: merchantId, ...(db || {}) };
}

/**
 * Construit un numéro de facture à partir du format configuré.
 * Jetons supportés : {PREFIX}, {YYYY}, {YY}, {MM}, {SEQ:N}
 * Exemple : « FAC-2026-00001 »
 */
export function buildInvoiceNumber(profile: CompanyProfile, seq: number): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const template = profile.invoice_number_format || 'FAC-{YYYY}-{SEQ:5}';
  return template
    .replace(/\{PREFIX\}/g, profile.invoice_prefix || 'FAC')
    .replace(/\{YYYY\}/g, String(year))
    .replace(/\{YY\}/g, String(year).slice(-2))
    .replace(/\{MM\}/g, month)
    .replace(/\{SEQ:(\d+)\}/g, (_m, n: string) => String(seq).padStart(parseInt(n, 10) || 1, '0'))
    .replace(/\{SEQ\}/g, String(seq));
}